import React from "react";
import { PublicLayout } from "../components/PublicLayout";
import { ShieldCheckIcon, EnvelopeSimpleIcon, InstagramLogoIcon } from "@phosphor-icons/react";

const Step = ({ number, title, children }) => (
  <li className="flex gap-4 rounded-sm border border-zinc-200 bg-white p-5">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-amber-500 font-display text-sm font-black text-zinc-950">
      {number}
    </span>
    <div className="flex-1">
      <h3 className="mb-2 font-display text-base font-bold tracking-tight text-zinc-950">
        {title}
      </h3>
      <div className="space-y-2 text-sm leading-relaxed text-zinc-700">{children}</div>
    </div>
  </li>
);

export default function DataDeletion() {
  return (
    <PublicLayout
      eyebrow="Legal"
      title="Data Deletion Instructions"
      subtitle="You have full control over the data Megatron processes. There are two ways to remove your data and revoke access."
    >
      <div className="mb-10 flex items-center gap-3 rounded-sm border border-amber-200 bg-amber-50 p-4">
        <ShieldCheckIcon size={24} weight="fill" className="shrink-0 text-amber-600" />
        <p className="text-sm text-amber-900">
          Megatron stores only Instagram comment metadata (comment IDs, usernames,
          comment text) used for delivering automated replies. We do not retain
          any data beyond what is necessary to operate the service.
        </p>
      </div>

      <h2 className="mb-4 font-display text-2xl font-bold tracking-tight text-zinc-950">
        Option 1 — Revoke Megatron's Access via Instagram
      </h2>
      <p className="mb-4 text-sm text-zinc-700">
        Once you revoke access, Instagram will immediately stop delivering any
        webhook events about your account or your comments to Megatron. Future
        comments will not be processed.
      </p>
      <ol className="space-y-3">
        <Step number="1" title="Open Instagram Apps & Websites settings">
          <p>
            On a desktop or mobile browser, log into Instagram and visit:
          </p>
          <p>
            <a
              href="https://www.instagram.com/accounts/manage_access/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 break-all text-amber-600 underline"
            >
              <InstagramLogoIcon size={14} weight="bold" />
              instagram.com/accounts/manage_access
            </a>
          </p>
        </Step>
        <Step number="2" title="Find Megatron in the list of authorized apps">
          <p>
            Scroll through "Active" or "Expired" connections. Megatron will be
            listed under the Instagram app name <strong>Megatron-IG</strong>.
          </p>
        </Step>
        <Step number="3" title="Tap Remove">
          <p>
            Tap or click <strong>Remove</strong> next to Megatron. Instagram will
            immediately revoke our access tokens and stop sending us webhook
            events about your account.
          </p>
        </Step>
      </ol>

      <h2 className="mt-12 mb-4 font-display text-2xl font-bold tracking-tight text-zinc-950">
        Option 2 — Request Deletion of Existing Logs
      </h2>
      <p className="mb-4 text-sm text-zinc-700">
        If you previously interacted with an account that uses Megatron and want
        any historical activity log entries about you (your username,
        commenter ID, or comment text) deleted from our records, email us:
      </p>

      <a
        href="mailto:connect@yogarubika.com?subject=Megatron%20Data%20Deletion%20Request"
        className="mb-6 inline-flex items-center gap-3 rounded-sm border border-zinc-300 bg-white px-5 py-3 font-mono text-sm text-zinc-900 hover:border-amber-500 hover:bg-amber-50"
        data-testid="data-deletion-email"
      >
        <EnvelopeSimpleIcon size={18} weight="duotone" className="text-amber-500" />
        connect@yogarubika.com
      </a>

      <p className="mb-4 text-sm text-zinc-700">
        In your email, please include:
      </p>
      <ul className="ml-5 list-disc space-y-1 text-sm text-zinc-700">
        <li>Your Instagram username</li>
        <li>
          (Optional) Specific comment IDs or post URLs you want removed from logs
        </li>
        <li>The reason for the request, if you'd like to share it</li>
      </ul>

      <p className="mt-6 text-sm text-zinc-700">
        We will confirm receipt within <strong>72 hours</strong> and complete
        deletion within <strong>30 days</strong>, in line with GDPR and Meta's
        Platform Terms.
      </p>

      <h2 className="mt-12 mb-4 font-display text-2xl font-bold tracking-tight text-zinc-950">
        What gets deleted?
      </h2>
      <ul className="ml-5 list-disc space-y-1 text-sm text-zinc-700">
        <li>Activity log entries containing your username or commenter ID</li>
        <li>Stored comment text from your interactions</li>
        <li>Any cached data associated with your IGSID</li>
      </ul>

      <h2 className="mt-12 mb-4 font-display text-2xl font-bold tracking-tight text-zinc-950">
        What does NOT get deleted?
      </h2>
      <ul className="ml-5 list-disc space-y-1 text-sm text-zinc-700">
        <li>
          Comments and DMs visible inside Instagram itself — only Instagram can
          remove those (use Instagram's own delete options)
        </li>
        <li>
          Aggregated, anonymized statistics that cannot be linked back to you
        </li>
      </ul>

      <p className="mt-12 text-xs text-zinc-500">
        Need additional help? See our{" "}
        <a href="/privacy" className="text-amber-600 underline">
          Privacy Policy
        </a>{" "}
        for full details on what we collect and why.
      </p>
    </PublicLayout>
  );
}
